import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltipShell } from "../panel";

const AXIS_COLOR = "var(--neutral-inactive)";

export interface TrendSeries {
  dataKey: string;
  name: string;
  color: string;
  dashed?: boolean;
}

function TrendTooltip({
  active,
  payload,
  label,
  suffix,
  prefix,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  suffix: string;
  prefix: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <ChartTooltipShell>
      <p className="font-semibold text-foreground">{label}</p>
      <div className="mt-1 space-y-0.5">
        {payload.map((entry) => (
          <p key={entry.name} className="flex items-center gap-2 text-muted-foreground">
            <span className="size-2" style={{ backgroundColor: entry.color }} aria-hidden />
            {entry.name}
            <span className="ml-auto tabular-nums text-foreground">
              {prefix}
              {entry.value.toFixed(2)}
              {suffix}
            </span>
          </p>
        ))}
      </div>
    </ChartTooltipShell>
  );
}

export function TrendLineChart<T extends { month: string }>({
  data,
  series,
  suffix = "%",
  prefix = "",
  height = 300,
}: {
  data: T[];
  series: TrendSeries[];
  suffix?: string;
  prefix?: string;
  height?: number;
}) {
  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(value: number) => `${prefix}${value.toFixed(1)}${suffix}`}
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            width={56}
          />
          <Tooltip content={<TrendTooltip suffix={suffix} prefix={prefix} />} />
          <Legend
            wrapperStyle={{ fontSize: 11, color: "var(--muted-foreground)" }}
            iconType="plainline"
          />
          {series.map((line) => (
            <Line
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={line.color}
              strokeWidth={1.75}
              strokeDasharray={line.dashed ? "4 3" : undefined}
              dot={false}
              activeDot={{ r: 3 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
